import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { X } from "lucide-react";

const badgeVariants = cva(
  "relative inline-flex items-center justify-center rounded-[4px] border font-semibold w-fit whitespace-nowrap shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow]",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-gradient-brand text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground border-2 border-black [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        outlineRounder:
          "text-foreground border-1 rounded-xl border-black [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
      size: {
        default: "px-2 py-0.5 text-sm gap-1 [&>svg]:size-3",
        sm: "px-1.5 py-0 text-xs gap-0.5 [&>svg]:size-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Badge({
  className,
  variant,
  size,
  onClose = null,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    closeable?: boolean;
    onClose?: null | (() => void);
  }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    >
      {!!onClose && (
        <button
          onClick={onClose}
          type="button"
          className="absolute z-0 w-5 h-5 bg-lemon top-0 right-0 translate-x-1/2 -translate-y-1/2 rounded-md flex justify-center items-center hover:bg-lemon-dark hover:text-white cursor-pointer"
        >
          <span className="sr-only">Close</span>
          <X className="size-4" />
        </button>
      )}
      <span className="overflow-hidden">{props.children}</span>
    </Comp>
  );
}

export { Badge, badgeVariants };
